import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Moment from "react-moment";
import moment from "moment-timezone";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  MapPinIcon,
  PhoneIcon,
  SparklesIcon,
  TruckIcon,
  UserIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { MarkerF } from "@react-google-maps/api";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  ToastError,
  ToastMarkedCancelledTrip,
  ToastWillCallActivated,
  ToastConfirmedTrip,
} from "utils/toasts";
import {
  searchTripsByMrn,
  searchConfirmedTripsByMrn,
  fetchConfigurations,
  fetchCooperateDetails,
  cancelTrip,
  fetchTripEta,
  fetchTripDetail,
  activateWillCall,
  updateIntakeStatus,
  fetchTripStats,
} from "services/memberMrn";
import { toIsoInTimeZone } from "services/tripPayload";
import { Dialog, DialogTitle, DialogBody } from "components/dialog";
import { DateTimePicker, toLocalString } from "components/DateTimePicker";
import { DateRangePicker } from "components/DateRangePicker";
import { MapView } from "components/MapView";
import pickupMarker from "images/pickup.png";
import dropoffMarker from "images/dropoff.png";

// Dedicated polling cadence for this page only — kept separate from any
// other page's refresh interval so they can be tuned independently.
const TRIPS_POLL_INTERVAL_MS = 30000;
// Status tabs for trips from GET /membermrn/trips are keyed with this prefix
// so they never collide with the main intake list's tabs.
const CONFIRMED_TAB_PREFIX = "confirmed:";

// Trips in one of these statuses are done — terminal, no further action
// possible.
const TERMINAL_TRIP_STATUSES = ["cancelled", "finished"];

// Converts a calendar Date from the date-range picker into the ISO instant
// for the start/end of that day in the member's own timeZone, so the range
// filter lines up with "that day" as the member's company sees it — not the
// browser's local zone, and not whatever default zone the API would
// otherwise assume from a bare "YYYY-MM-DD". Falls back to the browser's
// zone if the member's timeZone hasn't loaded yet.
function toDayBoundaryIso(date, timeZone, boundary) {
  if (!date) return undefined;
  const dateOnly = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
  const zone = timeZone || moment.tz.guess();
  const parsed = moment.tz(dateOnly, "YYYY-MM-DD", zone);
  return boundary === "end" ? parsed.endOf("day").toISOString() : parsed.startOf("day").toISOString();
}

// Human-readable label for an intake status key ("unconfirmed" shows as "Not Authorized").
function formatIntakeStatus(status) {
  if (status === "unconfirmed") return "Not Authorized";
  return (status || "").replace(/_/g, " ");
}

function canCancelTrip(trip) {
  if (trip.status === "cancelled" || trip.jobStatus === "cancelled") return false;
  // If trip is in staging (not confirmed), it should be unconfirmed, not cancelled
  if (trip.intakeStatus && trip.intakeStatus !== "confirmed") return false;
  const allowedStatuses = ["unassigned", "offered", "accepted", "pending"];
  return allowedStatuses.includes(trip.status);
}

function canConfirmIntake(trip) {
  // Confirm is only for accepting a suggested time, until it's confirmed or unconfirmed
  if (trip.status === "cancelled" || trip.jobStatus === "cancelled") return false;
  if (!trip.intakeSuggestedTime && !trip.suggested && trip.intakeStatus !== "suggested_plan") return false;
  return !["confirmed", "unconfirmed"].includes(trip.intakeStatus);
}

function canUnconfirmIntake(trip) {
  if (trip.status === "cancelled" || trip.jobStatus === "cancelled") return false;
  return Boolean(
    trip.intakeStatus &&
    trip.intakeStatus !== "unconfirmed" &&
    ["trip_intake", "suggested_plan"].includes(trip.intakeStatus)
  );
}

// A trip is a pending "Will Call" when both its pickup (scheduleTime) and
// appointment (appointmentTime) are pinned to 23:59 — the placeholder time
// used when the exact pickup isn't known yet, because the facility calls
// dispatch when the member is ready. Once activated (or once terminal),
// the button to activate it should no longer show. It also only makes sense
// to activate a Will Call trip on the day it's actually scheduled for — the
// facility calls dispatch same-day, so a future-dated trip can't be
// activated yet. All comparisons are done in the member's own timeZone
// (falling back to the browser's) so "today" means the member's today, not
// the staff browser's.
function isWillCallPending(trip, timeZone) {
  if (trip.intakeStatus === "unconfirmed" || trip.status === "intake") return false;
  if (trip.isWillCallActivated) return false;
  if (!trip.scheduleTime || !trip.appointmentTime) return false;

  const zone = timeZone || moment.tz.guess();
  const pickup = moment.tz(trip.scheduleTime, zone);
  const appointment = moment.tz(trip.appointmentTime, zone);
  const isWillCallTime =
    pickup.format("HH:mm") === "23:59" && appointment.format("HH:mm") === "23:59";
  if (!isWillCallTime) return false;

  const today = moment().tz(zone).format("YYYY-MM-DD");
  return pickup.format("YYYY-MM-DD") === today;
}

// Member's trip stats from GET /memberlink/trips/count: eligibility window,
// quota usage and per-intake-status counts.
function TripStatsPanel({ stats, timeZone, onClose }) {
  const { eligibility } = stats;
  const quota = eligibility?.tripQuota;
  const used = stats.used ?? 0;
  const usedPercent = quota ? Math.min(100, Math.round((used / quota) * 100)) : 0;
  const isEligible = eligibility?.status === "eligible";

  const counts = [
    ["Total", stats.total, "text-[#293562]"],
    ["Confirmed", stats.confirmed, "text-emerald-700"],
    ["Not Authorized", stats.unconfirmed, "text-amber-700"],
    ["Trip Intake", stats.trip_intake, "text-sky-700"],
  ];

  return (
    <div className="border-t border-[#293562]/10 bg-white/60 px-3.5 py-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <p className="text-xs font-semibold text-[#293562]">Trip Stats</p>
          {eligibility?.status && (
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-bold capitalize ${
                isEligible ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20" : "bg-red-50 text-red-700 ring-1 ring-red-600/20"
              }`}
            >
              {eligibility.status.replace(/_/g, " ")}
            </span>
          )}
          {eligibility?.startDate && eligibility?.endDate && (
            <span className="text-[11px] font-medium text-[#293562]/60">
              <Moment format="MMM D, YYYY" tz={timeZone || undefined}>{eligibility.startDate}</Moment>
              {" – "}
              <Moment format="MMM D, YYYY" tz={timeZone || undefined}>{eligibility.endDate}</Moment>
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Hide trip stats"
          className="shrink-0 text-[#293562]/40 hover:text-[#293562] transition p-1 rounded-lg hover:bg-[#293562]/5"
        >
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      </div>

      {quota != null && (
        <div>
          <div className="flex items-center justify-between text-[11px] font-semibold mb-1">
            <span className="text-[#293562]">
              {used} / {quota} trips used
            </span>
            {stats.limitReached ? (
              <span className="text-red-600">Limit reached</span>
            ) : (
              <span className="text-[#293562]/60">{stats.remaining ?? 0} remaining</span>
            )}
          </div>
          <div className="h-1.5 w-full rounded-full bg-[#293562]/10 overflow-hidden">
            <div
              className={`h-full rounded-full ${stats.limitReached ? "bg-red-500" : "bg-[#293562]"}`}
              style={{ width: `${usedPercent}%` }}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-4 gap-2">
        {counts.map(([label, value, color]) => (
          <div key={label} className="rounded-xl bg-white ring-1 ring-[#293562]/10 px-2 py-1.5 text-center">
            <p className={`text-base font-bold ${color}`}>{value ?? 0}</p>
            <p className="text-[10px] font-semibold text-[#293562]/50">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// A titled white card used for each section of the trip details modal.
function DetailSection({ title, icon: Icon, action, bodyClassName = "px-5 py-3", children }) {
  return (
    <section className="overflow-hidden rounded-lg bg-white shadow ring-1 ring-main-base">
      <div className="flex items-center justify-between gap-2 px-5 py-3 bg-main-base-50 border-b border-main-base">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-main-dark">
          {Icon && <Icon className="h-4 w-4 text-main-primary" />}
          {title}
        </h3>
        {action}
      </div>
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

// A single label/value pair inside a DetailSection grid.
function DetailField({ label, className = "", children }) {
  return (
    <div className={`min-w-0 py-2.5 border-b border-main-base/70 ${className}`}>
      <dt className="text-xs font-medium text-main-light">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-main-dark break-words">{children}</dd>
    </div>
  );
}

// Renders the room/floor/suite/phone/facility/modifier fields captured on
// origin/destination address additional info, skipping any that are empty.
function AddressAdditionalDetails({ info }) {
  if (!info) return null;

  const inlineFields = [
    ["Room", info.room],
    ["Floor", info.floor],
    ["Suite", info.aptOrSuite],
    ["Phone Number", info.phone],
  ].filter(([, value]) => value);

  return (
    <>
      {inlineFields.length > 0 && (
        <p className="text-[13px] text-main-light mt-1 font-medium">
          {inlineFields.map(([label, value], index) => (
            <span key={label} className={index > 0 ? "ml-3" : undefined}>
              {label}: <span className="text-main-dark">{value}</span>
            </span>
          ))}
        </p>
      )}
      {info.place && (
        <p className="text-[13px] text-main-light mt-1 font-medium">
          Home Facility Name: <span className="text-main-dark">{info.place}</span>
        </p>
      )}
      {info.modifier && (
        <p className="text-[13px] text-main-light mt-1 font-medium">
          Modifier One: <span className="text-main-dark">{info.modifier}</span>
        </p>
      )}
      {info.modifierTwo && (
        <p className="text-[13px] text-main-light mt-1 font-medium">
          Modifier Two: <span className="text-main-dark">{info.modifierTwo}</span>
        </p>
      )}
    </>
  );
}

export default function TripsListingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { fundingsourceId, memberId } = useParams();

  const [trips, setTrips] = useState([]);
  const [timeZone, setTimeZone] = useState(null);
  // Seeded from CreateMember when arriving right after creating a member,
  // as a fallback while this page's own fetch is in flight; replaced with
  // the authoritative `client` object the search response always includes
  // (even when the member has zero trips).
  const [member, setMember] = useState(location.state?.member ?? null);
  // Carries forward which field the user searched by on the previous
  // screen ("mrn" or "phoneNumber"), so polling/refetches here keep
  // querying the API the same way; defaults to "mrn" for direct links.
  const searchBy = location.state?.searchBy ?? "search";
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  // Trips from GET /membermrn/trips, shown as one tab per status; null until
  // the first load finishes.
  const [confirmedTrips, setConfirmedTrips] = useState(null);
  const isConfirmedTab = statusFilter.startsWith(CONFIRMED_TAB_PREFIX);
  // Pickup-date range filter, sent to the API as startDate/endDate so
  // filtering happens server-side rather than against the already-loaded
  // `trips` array (unlike statusFilter, which stays client-side). Defaults
  // to today's calendar date on both ends; once the member's timeZone loads,
  // toDayBoundaryIso re-resolves that same date against it (loadTrips
  // depends on `timeZone`, so the trip list refetches automatically).
  const [dateRangeFilter, setDateRangeFilter] = useState(() => {
    const today = new Date();
    return [today, today];
  });
  const [filterStartDate, filterEndDate] = dateRangeFilter;
  const [selectedTrip, setSelectedTrip] = useState(null);
  // Extra detail fetched from GET /memberlink/assigns/:assignId when the
  // modal opens — supplements `selectedTrip` (from the list) with fields
  // the list search doesn't return, like the linked client's own gender.
  // Status is deliberately NOT read from this — it must keep matching
  // whatever the trip card outside already shows, which is derived from
  // `selectedTrip` (the list item) via getDisplayStatus/getStatusClass.
  const [tripDetail, setTripDetail] = useState(null);
  // Trip pending cancel confirmation, set from the card's Cancel button.
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  // Trip pending Will Call activation, set from the card's Will Call button.
  const [willCallTarget, setWillCallTarget] = useState(null);
  const [willCallPreScheduleTime, setWillCallPreScheduleTime] = useState("");
  const [willCallNote, setWillCallNote] = useState("");
  const [activatingWillCall, setActivatingWillCall] = useState(false);
  // Trip pending intake confirmation, set from the card's/detail's Confirm button.
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirming, setConfirming] = useState(false);
  // Trip pending intake unconfirmation, set from the card's/detail's Unconfirm button.
  const [unconfirmTarget, setUnconfirmTarget] = useState(null);
  const [unconfirming, setUnconfirming] = useState(false);
  // Funding source's trip stats, fetched on demand from the header button.
  const [tripStats, setTripStats] = useState(null);
  const [fetchingTripStats, setFetchingTripStats] = useState(false);
  const [etaMessage, setEtaMessage] = useState(null);
  const [fetchingEta, setFetchingEta] = useState(false);
  const [configs, setConfigs] = useState({
    serviceAreas: [],
    levelOfServices: [],
    spaceTypes: [],
  });
  // Seeded from the search screen (and forwarded through CreateMember /
  // CreateTrip) so we don't re-fetch the same funding source on every
  // visit; only hit the API as a fallback for direct links or refreshes.
  const [fundingSource, setFundingSource] = useState(
    location.state?.fundingSource ?? null
  );

  const hasErroredRef = useRef(false);

  // Used to resolve the level of service / space type / service area IDs
  // stored on a trip into their human-readable titles for the detail panel.
  const levelOfServiceTitleById = useMemo(
    () => Object.fromEntries(configs.levelOfServices.map((item) => [item._id, item.title])),
    [configs.levelOfServices]
  );
  const spaceTypeTitleById = useMemo(
    () => Object.fromEntries(configs.spaceTypes.map((item) => [item._id, item.title])),
    [configs.spaceTypes]
  );

  const loadTrips = useCallback(
    async ({ silent } = {}) => {
      if (!silent) setLoading(true);
      try {
        const data = await searchTripsByMrn(memberId, fundingsourceId, searchBy, {
          startDate: toDayBoundaryIso(filterStartDate, timeZone, "start"),
          endDate: toDayBoundaryIso(filterEndDate, timeZone, "end"),
        });
        if (data === null) {
          ToastError(`Member "${memberId}" not found`);
          navigate(`/fundingsource/${fundingsourceId}`, { replace: true });
          return;
        }
        const sortedTrips = [...(data.trips || [])].sort((a, b) => {
          const timeA = a.scheduleTime ? new Date(a.scheduleTime).getTime() : 0;
          const timeB = b.scheduleTime ? new Date(b.scheduleTime).getTime() : 0;
          return timeA - timeB;
        });
        setTrips(sortedTrips);
        setTimeZone(data.timeZone || null);
        if (data.client) {
          const homeAddress = data.client.homeAddress || {};
          setMember({
            id: data.client._id ?? null,
            clientId: data.client.clientId ?? memberId,
            displayName:
              data.client.displayName ||
              `${data.client.firstName || ""} ${data.client.lastName || ""}`.trim(),
            contactNumber: data.client.contactNumber || "",
            cellPhone: data.client.cellPhone || "",
            email: data.client.email || "",
            dob: data.client.dob || "",
            gender: data.client.gender || "",
            address: homeAddress.address || "",
            addressLat: homeAddress.latitude ?? null,
            addressLng: homeAddress.longitude ?? null,
            addressZip: homeAddress.zipCode || "",
          });
        }
        hasErroredRef.current = false;
      } catch (error) {
        if (!hasErroredRef.current) {
          ToastError(error.message || "Unable to load trips");
          hasErroredRef.current = true;
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [fundingsourceId, memberId, searchBy, filterStartDate, filterEndDate, timeZone]
  );

  const loadConfirmedTrips = useCallback(
    async ({ silent } = {}) => {
      try {
        const data = await searchConfirmedTripsByMrn(memberId, fundingsourceId, searchBy, {
          startDate: toDayBoundaryIso(filterStartDate, timeZone, "start"),
          endDate: toDayBoundaryIso(filterEndDate, timeZone, "end"),
        });
        const sortedTrips = [...(data.trips || [])].sort((a, b) => {
          const timeA = a.scheduleTime ? new Date(a.scheduleTime).getTime() : 0;
          const timeB = b.scheduleTime ? new Date(b.scheduleTime).getTime() : 0;
          return timeA - timeB;
        });
        setConfirmedTrips(sortedTrips);
      } catch (error) {
        if (!silent) ToastError(error.message || "Unable to load confirmed trips");
      }
    },
    [fundingsourceId, memberId, searchBy, filterStartDate, filterEndDate, timeZone]
  );

  // Refreshes both trip lists after an action.
  const reloadTrips = useCallback(() => {
    loadTrips({ silent: true });
    loadConfirmedTrips({ silent: true });
  }, [loadTrips, loadConfirmedTrips]);

  useEffect(() => {
    if (!fundingsourceId || !memberId) return;

    loadConfirmedTrips();
    const intervalId = setInterval(
      () => loadConfirmedTrips({ silent: true }),
      TRIPS_POLL_INTERVAL_MS
    );

    return () => clearInterval(intervalId);
  }, [fundingsourceId, memberId, loadConfirmedTrips]);

  useEffect(() => {
    if (!fundingsourceId || !memberId) return;

    loadTrips();
    const intervalId = setInterval(
      () => loadTrips({ silent: true }),
      TRIPS_POLL_INTERVAL_MS
    );

    return () => clearInterval(intervalId);
  }, [fundingsourceId, memberId, loadTrips]);

  // Needed to resolve los/spaceType/serviceArea IDs in the trip detail
  // panel into readable titles; fetched once since these rarely change.
  useEffect(() => {
    if (!fundingsourceId) return;
    let cancelled = false;
    fetchConfigurations(fundingsourceId)
      .then((data) => {
        if (!cancelled) setConfigs(data);
      })
      .catch(() => { });
    return () => {
      cancelled = true;
    };
  }, [fundingsourceId]);

  useEffect(() => {
    if (!fundingsourceId) return;
    let cancelled = false;
    fetchCooperateDetails(fundingsourceId)
      .then((data) => {
        if (!cancelled) setFundingSource(data);
      })
      .catch(() => { });
    return () => {
      cancelled = true;
    };
  }, [fundingsourceId]);

  const fundingSourceName =
    fundingSource?.displayName ||
    fundingSource?.cooperateName ||
    fundingSource?.companyName ||
    "";



  function handleCreateTrip() {
    navigate(`/fundingsource/${fundingsourceId}/trips/create/${memberId}`, {
      state: { timeZone, member, searchBy, fundingSource },
    });
  }

  async function handleCancelTrip() {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      if (cancelTarget.intakeStatus && cancelTarget.intakeStatus !== "confirmed") {
        await updateIntakeStatus(cancelTarget._id, fundingsourceId, "unconfirmed");
        ToastMarkedCancelledTrip();
        if (selectedTrip && selectedTrip._id === cancelTarget._id) {
          setSelectedTrip((prev) => (prev ? { ...prev, intakeStatus: "unconfirmed" } : prev));
        }
      } else {
        await cancelTrip(cancelTarget._id, fundingsourceId);
        ToastMarkedCancelledTrip();
        if (selectedTrip && selectedTrip._id === cancelTarget._id) {
          setSelectedTrip((prev) => (prev ? { ...prev, status: "cancelled", jobStatus: "cancelled" } : prev));
        }
      }
      setCancelTarget(null);
      reloadTrips();
    } catch (error) {
      ToastError(error.message || "Unable to Cancel");
    } finally {
      setCancelling(false);
    }
  }

  function openWillCallPopup(trip) {
    setWillCallTarget(trip);
    setWillCallPreScheduleTime(
      timeZone ? moment().tz(timeZone).format("YYYY-MM-DDTHH:mm") : toLocalString(new Date())
    );
    setWillCallNote(trip.companyNote || "");
  }

  function closeWillCallPopup() {
    if (activatingWillCall) return;
    setWillCallTarget(null);
    setWillCallPreScheduleTime("");
    setWillCallNote("");
  }

  async function handleActivateWillCall() {
    if (!willCallTarget || !willCallPreScheduleTime) return;
    setActivatingWillCall(true);
    try {
      await activateWillCall(willCallTarget._id, fundingsourceId, {
        preScheduleTime: toIsoInTimeZone(willCallPreScheduleTime, timeZone),
        companyNote: willCallNote,
      });
      ToastWillCallActivated();
      setWillCallTarget(null);
      setWillCallPreScheduleTime("");
      setWillCallNote("");
      reloadTrips();
    } catch (error) {
      ToastError(error.message || "Unable to activate Will Call trip");
    } finally {
      setActivatingWillCall(false);
    }
  }

  async function handleConfirmIntake() {
    if (!confirmTarget) return;
    setConfirming(true);
    try {
      await updateIntakeStatus(confirmTarget._id, fundingsourceId, "confirmed");
      ToastConfirmedTrip();
      if (selectedTrip && selectedTrip._id === confirmTarget._id) {
        setSelectedTrip((prev) => ({
          ...prev,
          intakeStatus: "confirmed",
        }));
      }
      setConfirmTarget(null);
      reloadTrips();
    } catch (error) {
      ToastError(error.message || "Unable to confirm trip");
    } finally {
      setConfirming(false);
    }
  }

  async function handleUnconfirmIntake() {
    if (!unconfirmTarget) return;
    setUnconfirming(true);
    try {
      await updateIntakeStatus(unconfirmTarget._id, fundingsourceId, "unconfirmed");
      ToastMarkedCancelledTrip();
      if (selectedTrip && selectedTrip._id === unconfirmTarget._id) {
        setSelectedTrip((prev) => (prev ? { ...prev, intakeStatus: "unconfirmed" } : prev));
      }
      setUnconfirmTarget(null);
      reloadTrips();
    } catch (error) {
      ToastError(error.message || "Unable to Cancel");
    } finally {
      setUnconfirming(false);
    }
  }

  function openTripDetails(trip) {
    setSelectedTrip(trip);
    setEtaMessage(null);
    setTripDetail(null);
    fetchTripDetail(trip._id, fundingsourceId)
      .then((data) => setTripDetail(data))
      .catch(() => {});
  }

  function closeTripDetails() {
    setSelectedTrip(null);
    setTripDetail(null);
  }

  async function handleFetchEta() {
    if (!selectedTrip) return;
    setFetchingEta(true);
    try {
      const message = await fetchTripEta(selectedTrip._id, fundingsourceId);
      setEtaMessage(message || "No ETA available right now.");
    } catch (error) {
      ToastError(error.message || "Unable to fetch ETA");
    } finally {
      setFetchingEta(false);
    }
  }

  async function handleFetchTripStats() {
    setFetchingTripStats(true);
    try {
      setTripStats(await fetchTripStats(memberId, fundingsourceId, searchBy));
    } catch (error) {
      ToastError(error.message || "Unable to fetch trip stats");
    } finally {
      setFetchingTripStats(false);
    }
  }

  function handleMapLoad(map) {
    if (!selectedTrip) return;
    const origin = {
      lat: selectedTrip.jobOriginLatitude,
      lng: selectedTrip.jobOriginLongitude,
    };
    const destination = {
      lat: selectedTrip.jobDestinationLatitude,
      lng: selectedTrip.jobDestinationLongitude,
    };
    if (
      typeof origin.lat !== "number" ||
      typeof origin.lng !== "number" ||
      typeof destination.lat !== "number" ||
      typeof destination.lng !== "number"
    ) {
      return;
    }

    if (origin.lat === destination.lat && origin.lng === destination.lng) {
      map.panTo(origin);
      map.setZoom(14);
      return; // No need to draw a route if origin is same as destination
    }

    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(origin);
    bounds.extend(destination);
    map.fitBounds(bounds);


  }

  const hasRouteCoordinates =
    !!selectedTrip &&
    typeof selectedTrip.jobOriginLatitude === "number" &&
    typeof selectedTrip.jobOriginLongitude === "number" &&
    typeof selectedTrip.jobDestinationLatitude === "number" &&
    typeof selectedTrip.jobDestinationLongitude === "number";

  const losTitle = selectedTrip ? levelOfServiceTitleById[selectedTrip.los] : null;
  const spaceTitle = selectedTrip ? spaceTypeTitleById[selectedTrip.spaceType] : null;
  const levelOfServiceLabel = losTitle
    ? spaceTitle
      ? `${losTitle} (${spaceTitle})`
      : losTitle
    : "—";
  const milesValue = selectedTrip?.milage;
  const milesLabel =
    milesValue === null || milesValue === undefined
      ? "—"
      : `${Number.isInteger(milesValue) ? milesValue : milesValue.toFixed(1)} mi`;

  const cancelReason = selectedTrip?.changeReason || tripDetail?.changeReason;

  const getDisplayStatus = (trip) => {
    if (['unassigned','standing'].includes(trip.status) && trip.intakeStatus && trip.intakeStatus !== "confirmed") {
      return formatIntakeStatus(trip.intakeStatus);
    }


    if (trip.status === "inprogress") {
      if (trip.jobStatus === "started") {
        return "Member Onboard";
      }

      return trip.jobStatus || trip.status;
    } else if (trip.jobStatus === "pending") {
      return "Unassigned";
    }

    return trip.status;
  };

  const getStatusClass = (trip) => {
    const displayStatus = (getDisplayStatus(trip) || "").toLowerCase();
    if (displayStatus.includes("onboard")) {
      return "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-700/10";
    }
    if (displayStatus.includes("suggested")) {
      return "bg-[#FFF7ED] text-[#C2410C] ring-1 ring-[#C2410C]/30";
    }
    if (displayStatus.includes("intake") || displayStatus.includes("not authorized")) {
      return "bg-amber-50 text-amber-700 ring-1 ring-amber-600/15";
    }
    if (displayStatus.includes("pending")) {
      return "bg-blue-50 text-blue-700 ring-1 ring-blue-700/10";
    }
    if (displayStatus.includes("unassigned")) {
      return "bg-purple-50 text-purple-700 ring-1 ring-purple-700/10";
    }
    if (
      displayStatus.includes("inprogress") ||
      displayStatus.includes("onway") ||
      displayStatus.includes("arrived")
    ) {
      return "bg-sky-50 text-sky-700 ring-1 ring-sky-700/10";
    }
    if (displayStatus.includes("confirmed")) {
      return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/10";
    }
    if (displayStatus.includes("cancelled")) {
      return "bg-red-50 text-red-700 ring-1 ring-red-600/10";
    }
    if (displayStatus.includes("finished")) {
      return "bg-gray-50 text-gray-600 ring-1 ring-gray-500/10";
    }
    return "bg-gray-50 text-gray-600 ring-1 ring-gray-500/10";
  };

  const availableStatuses = useMemo(() => {
    const statuses = new Set();
    trips.forEach((trip) => {
      const display = getDisplayStatus(trip);
      if (display) {
        // Normalizes to match exact text rendered in the badge
        statuses.add(display);
      }
    });
    return ["All", ...Array.from(statuses)];
  }, [trips]);

  // One tab per status found in the membermrn/trips response, lower-cased so
  // "Unassigned"/"unassigned" share one tab.
  const confirmedStatuses = useMemo(() => {
    const statuses = new Set();
    (confirmedTrips || []).forEach((trip) => {
      const display = (getDisplayStatus(trip) || "").toLowerCase();
      if (display) statuses.add(display);
    });
    return Array.from(statuses).map((status) => CONFIRMED_TAB_PREFIX + status);
  }, [confirmedTrips]);

  const filteredTrips = useMemo(() => {
    if (statusFilter.startsWith(CONFIRMED_TAB_PREFIX)) {
      const status = statusFilter.slice(CONFIRMED_TAB_PREFIX.length);
      return (confirmedTrips || []).filter((trip) => (getDisplayStatus(trip) || "").toLowerCase() === status);
    }
    if (statusFilter === "All") return trips;
    return trips.filter((trip) => getDisplayStatus(trip) === statusFilter);
  }, [trips, confirmedTrips, statusFilter]);

  const getFilterCount = (statusLabel) => {
    if (statusLabel.startsWith(CONFIRMED_TAB_PREFIX)) {
      const status = statusLabel.slice(CONFIRMED_TAB_PREFIX.length);
      return (confirmedTrips || []).filter((trip) => (getDisplayStatus(trip) || "").toLowerCase() === status).length;
    }
    if (statusLabel === "All") return trips.length;
    return trips.filter((trip) => getDisplayStatus(trip) === statusLabel).length;
  };

  const tripClient = selectedTrip?.client || selectedTrip?.priorityClient;
  const tripClientName = tripClient
    ? tripClient.displayName ||
      `${tripClient.firstName || ""} ${tripClient.lastName || ""}`.trim()
    : "";

  return (
    <section className="min-h-dvh bg-gradient-to-br from-[#eef1f8] via-[#f5f5f7] to-[#e9ecf3] flex items-center justify-center p-4">
      <div className="w-full max-w-lg h-[calc(100dvh-2rem)] bg-white/90 backdrop-blur-xl rounded-3xl shadow-[0_10px_40px_rgb(0,0,0,0.08)] ring-1 ring-black/5 flex flex-col overflow-hidden">

        {/* Fixed top — never scrolls */}
        <div className="shrink-0 px-6 pt-4 pb-3 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => navigate(`/fundingsource/${fundingsourceId}`)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition shrink-0"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back
            </button>
            <div className="flex items-center gap-1.5 min-w-0">
              <div className="flex items-center gap-1 min-w-0">
                <DateRangePicker
                  startDate={filterStartDate}
                  endDate={filterEndDate}
                  onChange={(start, end) => setDateRangeFilter([start, end])}
                  placeholder="Date range"
                  className="rounded-xl bg-gray-100 px-2.5 py-2 text-xs font-medium text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#293562] focus:bg-white transition shrink-0"
                />
                {(filterStartDate || filterEndDate) && (
                  <button
                    type="button"
                    onClick={() => setDateRangeFilter([null, null])}
                    aria-label="Clear date range"
                    className="shrink-0 text-gray-400 hover:text-gray-600 transition p-1 rounded-lg hover:bg-gray-100"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {trips.length > 0 && (
                <button
                  type="button"
                  onClick={handleCreateTrip}
                  className="shrink-0 rounded-xl bg-[#293562] px-3 py-2 text-sm font-semibold text-white hover:brightness-95 transition"
                >
                  New Trip
                </button>
              )}
            </div>
          </div>

          <div className="rounded-2xl border-2 border-[#293562]/20 overflow-hidden bg-[#293562]/5">
            {fundingSource && (
              <div className="px-3.5 py-2.5 flex items-center gap-3 border-b border-[#293562]/10">
                <div className="h-10 w-10 shrink-0 rounded-xl bg-white ring-1 ring-[#293562]/10 flex items-center justify-center text-[#293562] text-sm font-bold overflow-hidden">
                  {fundingSource.profileImageURL ? (
                    <img
                      src={fundingSource.profileImageURL}
                      alt={fundingSourceName || "Logo"}
                      className="h-full w-full object-contain p-0.5"
                    />
                  ) : (
                    (fundingSourceName || "F").charAt(0).toUpperCase()
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#293562] truncate">
                    {fundingSourceName}
                  </p>
                  {/* {(fundingSource.contactNumber || fundingSource.email) && (
                    <p className="text-[11px] font-medium text-[#293562]/60 truncate">
                      {[fundingSource.contactNumber, fundingSource.email]
                        .filter(Boolean)
                        .join("  ·  ")}
                    </p>
                  )} */}
                </div>
                <button
                  type="button"
                  onClick={handleFetchTripStats}
                  disabled={fetchingTripStats}
                  className="ml-auto shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-white ring-1 ring-[#293562]/15 px-3 py-1.5 text-xs font-semibold text-[#293562] hover:bg-[#293562]/5 transition disabled:opacity-50"
                >
                  {fetchingTripStats ? "Checking..." : "Check Eligibility"}
                </button>
              </div>
            )}

            <div className="grid grid-cols-3 divide-x divide-[#293562]/10">
              <div className="px-3 py-2">
                <p className="text-[10px] font-semibold tracking-wider text-[#293562]/50 mb-0.5">Name</p>
                <p className="text-sm font-semibold text-[#293562] truncate">{member?.displayName || "—"}</p>
              </div>
              <div className="px-3 py-2">
                <p className="text-[10px] font-semibold tracking-wider text-[#293562]/50 mb-0.5">MRN</p>
                <p className="text-sm font-semibold text-[#293562] truncate">{member?.clientId || memberId}</p>
              </div>
              <div className="px-3 py-2">
                <p className="text-[10px] font-semibold tracking-wider text-[#293562]/50 mb-0.5">Gender</p>
                <p className="text-sm font-semibold text-[#293562] capitalize">{member?.gender || "—"}</p>
              </div>
            </div>

            {tripStats && (
              <TripStatsPanel
                stats={tripStats}
                timeZone={timeZone}
                onClose={() => setTripStats(null)}
              />
            )}
          </div>

          {/* Status Filters */}
          {!loading && (
            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-2 shrink-0 border-b border-gray-100/80 scroll-smooth">
              {[...availableStatuses, ...confirmedStatuses].map((status, index) => {
                const count = getFilterCount(status);
                const isActive = statusFilter === status;
                const isFirstConfirmed = index === availableStatuses.length;
                return (
                  <React.Fragment key={status}>
                  {isFirstConfirmed && <span className="shrink-0 h-5 w-px bg-gray-200 mx-1" aria-hidden="true" />}
                  <button
                    type="button"
                    onClick={() => setStatusFilter(status)}
                    className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold transition flex items-center gap-1.5 ${
                      isActive
                        ? "bg-[#293562] text-white shadow-sm"
                        : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                    }`}
                  >
                    <span className="capitalize">{status.replace(CONFIRMED_TAB_PREFIX, "")}</span>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold leading-none ${
                        isActive ? "bg-white/20 text-white" : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        {/* Scrollable trips area — scrollbar sits at the card edge */}
        <div className="flex-1 overflow-y-auto min-h-0 px-6 pb-6">
          {loading ? (
            <SkeletonTheme baseColor="#f3f4f6" highlightColor="#e5e7eb">
              <div className="space-y-3">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="rounded-2xl bg-gray-100 p-4">
                    <Skeleton height={16} width="50%" />
                    <div className="mt-3 space-y-2">
                      <Skeleton height={14} width="80%" />
                      <Skeleton height={14} width="65%" />
                    </div>
                  </div>
                ))}
              </div>
            </SkeletonTheme>
          ) : trips.length === 0 && !isConfirmedTab ? (
            <div className="flex flex-col items-center text-center space-y-3 py-4">
              <p className="text-sm text-gray-500 max-w-xs">
                This member doesn't have any trips scheduled.
              </p>
              <button
                type="button"
                onClick={handleCreateTrip}
                className="w-full rounded-2xl bg-[#293562] py-4 text-lg font-semibold text-white shadow-sm hover:brightness-95 transition"
              >
                Create Trip
              </button>
            </div>
          ) : filteredTrips.length === 0 ? (
            <div className="flex flex-col items-center text-center space-y-2 py-8">
              <p className="text-sm text-gray-500 max-w-xs">
                No trips match the selected status filter.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredTrips.map((trip, index) => (
                <div
                  key={trip._id}
                  onClick={() => openTripDetails(trip)}
                  className="rounded-2xl bg-gray-100 p-4 space-y-3 cursor-pointer transition hover:bg-gray-200/80"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2.5 mb-1 flex-wrap">
                        <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-md bg-[#293562]/10 text-[#293562] text-[10px] font-bold tracking-wider">
                          Trip {index + 1}
                        </span>
                        <div className="text-sm font-semibold text-gray-900">
                          <Moment format="ddd, D MMM YYYY · h:mm A" tz={timeZone || undefined}>
                            {trip.scheduleTime}
                          </Moment>
                        </div>
                        {trip.intakeSuggestedTime && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#FFF7ED] text-[#C2410C] border border-[#C2410C] text-[10px] font-bold tracking-wide">
                            Sugg. Time:{" "}
                            <Moment format="ddd, D MMM YYYY · h:mm A" tz={timeZone || undefined} className="ml-1">
                              {trip.intakeSuggestedTime}
                            </Moment>
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 pl-0.5">{trip.tripId}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${getStatusClass(trip)}`}
                      >{getDisplayStatus(trip)}</span>
                    </div>
                  </div>
                  <div className="relative space-y-3">
                    <div className="absolute left-3 top-6 bottom-6 w-px border-l border-dashed border-gray-300" />
                    <div className="relative flex items-center gap-2.5">
                      <div className="h-6 w-6 shrink-0 rounded-full bg-white shadow-sm flex items-center justify-center z-10">
                        <MapPinIcon className="h-3.5 w-3.5 text-green-500" />
                      </div>
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {trip.jobOriginAddress}
                      </p>
                    </div>
                    <div className="relative flex items-center gap-2.5">
                      <div className="h-6 w-6 shrink-0 rounded-full bg-white shadow-sm flex items-center justify-center z-10">
                        <MapPinIcon className="h-3.5 w-3.5 text-red-500" />
                      </div>
                      <p className="text-sm font-semibold text-gray-600 truncate">
                        {trip.jobDestinationAddress}
                      </p>
                    </div>
                  </div>
                  {trip.changeReason && (
                    <div className="rounded-xl bg-red-50 border border-red-100/80 px-3 py-2 text-xs text-red-700 flex items-start gap-1.5">
                      <span className="font-semibold text-red-800 shrink-0">Cancel Reason:</span>
                      <span className="break-words">{trip.changeReason}</span>
                    </div>
                  )}
                  {(canCancelTrip(trip) ||
                    isWillCallPending(trip, timeZone) ||
                    canConfirmIntake(trip) ||
                    canUnconfirmIntake(trip)) && (
                    <div className="flex justify-end gap-2 pt-1 flex-wrap">
                      {canConfirmIntake(trip) && (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setConfirmTarget(trip);
                          }}
                          className="text-xs font-semibold bg-emerald-600 text-white px-3 py-1.5 rounded-lg hover:bg-emerald-700 transition shadow-sm"
                        >
                          Confirm
                        </button>
                      )}
                      {canUnconfirmIntake(trip) && (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setUnconfirmTarget(trip);
                          }}
                          className="text-xs font-semibold bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 transition shadow-sm"
                        >
                          Cancel
                        </button>
                      )}
                      {isWillCallPending(trip, timeZone) && (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openWillCallPopup(trip);
                          }}
                          className="text-xs font-semibold bg-[#293562] text-white px-3 py-1.5 rounded-lg hover:bg-[#1e274a] transition shadow-sm"
                        >
                          Will Call
                        </button>
                      )}
                      {canCancelTrip(trip) && (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setCancelTarget(trip);
                          }}
                          className="text-xs font-semibold bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 transition shadow-sm"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={!!selectedTrip}
        onClose={closeTripDetails}
        size="5xl"
        className="flex flex-col max-h-[90vh] overflow-hidden !p-0 sm:!rounded-lg"
      >
        {selectedTrip && (() => {
          const suggestedTime = selectedTrip.intakeSuggestedTime || tripDetail?.intakeSuggestedTime;
          const suggestedPlan = selectedTrip.suggested || tripDetail?.suggested;
          const suggestedLosTitle = suggestedPlan?.los ? levelOfServiceTitleById[suggestedPlan.los] : null;
          const suggestedSpaceTitle = suggestedPlan?.spaceType ? spaceTypeTitleById[suggestedPlan.spaceType] : null;
          const suggestedLosLabel = suggestedLosTitle
            ? suggestedSpaceTitle
              ? `${suggestedLosTitle} (${suggestedSpaceTitle})`
              : suggestedLosTitle
            : "—";

          return (
            <>
              {/* Header: trip identity, status and actions */}
              <div className="flex-shrink-0 flex flex-col gap-4 px-6 py-4 bg-white border-b border-main-base sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-11 w-11 shrink-0 rounded-lg bg-main-primary/10 text-main-primary flex items-center justify-center">
                    <TruckIcon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <DialogTitle className="!text-base font-semibold text-main-dark">Trip Details</DialogTitle>
                      <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-medium capitalize ${getStatusClass(selectedTrip)}`}>
                        {getDisplayStatus(selectedTrip)}
                      </span>
                    </div>
                    <p className="mt-0.5 flex items-center gap-1.5 flex-wrap text-xs text-main-light">
                      <span className="font-mono font-medium text-main-secondary">{selectedTrip.tripId || "—"}</span>
                      {selectedTrip.createdAt && (
                        <>
                          <span className="text-main-base">•</span>
                          <span>
                            Created{" "}
                            <Moment format="MMM D, YYYY · h:mm A" tz={timeZone || undefined}>{selectedTrip.createdAt}</Moment>
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {canConfirmIntake({ ...selectedTrip, intakeSuggestedTime: suggestedTime, suggested: suggestedPlan }) && (
                    <button
                      type="button"
                      onClick={() => setConfirmTarget(selectedTrip)}
                      className="btn-success text-xs"
                    >
                      <CheckCircleIcon className="h-4 w-4" />
                      Confirm
                    </button>
                  )}
                  {canUnconfirmIntake(selectedTrip) && (
                    <button
                      type="button"
                      onClick={() => setUnconfirmTarget(selectedTrip)}
                      className="btn-danger text-xs"
                    >
                      Cancel
                    </button>
                  )}
                  {canCancelTrip(selectedTrip) && (
                    <button
                      type="button"
                      onClick={() => setCancelTarget(selectedTrip)}
                      className="btn-danger text-xs"
                    >
                      Cancel
                    </button>
                  )}
                  {selectedTrip.jobStatus === "onway" && (
                    <button
                      type="button"
                      onClick={handleFetchEta}
                      disabled={fetchingEta}
                      className="btn-white text-xs"
                    >
                      <ClockIcon className="h-4 w-4" />
                      {fetchingEta ? "Fetching ETA..." : "Fetch ETA"}
                    </button>
                  )}
                  {selectedTrip.status === "inprogress" && (
                    <button
                      type="button"
                      onClick={() => window.open(`/trip-tracking/${selectedTrip._id}`, "_blank")}
                      className="btn-primary text-xs"
                    >
                      <MapPinIcon className="h-4 w-4" />
                      Track Trip
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={closeTripDetails}
                    aria-label="Close"
                    className="ml-1 text-main-light hover:text-main-secondary transition outline-none p-1.5 rounded-md hover:bg-main-base-100"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Key facts at a glance */}
              <div className="flex-shrink-0 grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-main-base border-b border-main-base bg-main-base-50">
                <div className="px-6 py-3">
                  <p className="text-[11px] font-medium text-main-light">Pickup</p>
                  <p className="mt-0.5 text-sm font-semibold text-main-dark">
                    <Moment format="MMM D · h:mm A" tz={timeZone || undefined}>{selectedTrip.scheduleTime}</Moment>
                  </p>
                </div>
                <div className="px-6 py-3">
                  <p className="text-[11px] font-medium text-main-light">Appointment</p>
                  <p className="mt-0.5 text-sm font-semibold text-main-dark">
                    <Moment format="MMM D · h:mm A" tz={timeZone || undefined}>{selectedTrip.appointmentTime}</Moment>
                  </p>
                </div>
                <div className="px-6 py-3">
                  <p className="text-[11px] font-medium text-main-light">Distance (Est.)</p>
                  <p className="mt-0.5 text-sm font-semibold text-main-primary">{milesLabel}</p>
                </div>
                <div className="px-6 py-3 min-w-0">
                  <p className="text-[11px] font-medium text-main-light">Level of Service</p>
                  <p className="mt-0.5 text-sm font-semibold text-main-dark truncate">{levelOfServiceLabel}</p>
                </div>
              </div>

              {etaMessage && (
                <div className="flex-shrink-0 flex items-center gap-2 px-6 py-2.5 bg-blue-50 border-b border-blue-100 text-sm font-medium text-blue-800">
                  <ClockIcon className="h-4 w-4 shrink-0 text-blue-500" />
                  {etaMessage}
                </div>
              )}

              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto bg-main-base-100 p-4 sm:p-6">
                <DialogBody className="!mt-0 space-y-5">
                  {cancelReason && (
                    <div className="flex items-start gap-3 rounded-lg bg-main-danger-100/60 ring-1 ring-main-danger/20 px-4 py-3">
                      <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-main-danger" />
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-main-danger">Cancel Reason</p>
                        <p className="mt-0.5 text-sm text-main-dark break-words">{cancelReason}</p>
                      </div>
                    </div>
                  )}

                  {suggestedPlan && (
                    <section className="overflow-hidden rounded-lg bg-white shadow ring-1 ring-[#D0AA00]/40">
                      <div className="flex items-center justify-between gap-2 px-5 py-3 bg-[#FFF8E1] border-b border-[#D0AA00]/30">
                        <h3 className="flex items-center gap-2 text-sm font-semibold text-[#8A6D00]">
                          <SparklesIcon className="h-4 w-4" />
                          Suggested Plan
                        </h3>
                        <span className="text-[11px] font-medium text-[#8A6D00]/80">Review the changes before confirming</span>
                      </div>
                      <div className="p-5 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {[
                            ["Pickup Time", selectedTrip.scheduleTime, suggestedPlan.scheduleTime],
                            ["Appointment Time", selectedTrip.appointmentTime, suggestedPlan.appointmentTime],
                          ].map(([label, current, suggested]) => (
                            <div key={label} className="rounded-md bg-main-base-50 ring-1 ring-main-base px-3 py-2.5">
                              <p className="text-[11px] font-medium text-main-light">{label}</p>
                              <div className="mt-1 flex items-center gap-2 flex-wrap text-sm">
                                <span className="text-main-light line-through">
                                  {current ? <Moment format="MMM D · h:mm A" tz={timeZone || undefined}>{current}</Moment> : "—"}
                                </span>
                                <ArrowRightIcon className="h-3.5 w-3.5 text-main-light" />
                                <span className="font-semibold text-[#8A6D00]">
                                  {suggested ? <Moment format="MMM D · h:mm A" tz={timeZone || undefined}>{suggested}</Moment> : "—"}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                          <DetailField label="Pickup Address" className="sm:col-span-2">
                            {suggestedPlan.jobOriginAddress || "—"}
                          </DetailField>
                          <DetailField label="Dropoff Address" className="sm:col-span-2">
                            {suggestedPlan.jobDestinationAddress || "—"}
                          </DetailField>
                          <DetailField label="Level of Service" className="sm:col-span-2">{suggestedLosLabel}</DetailField>
                        </dl>
                        {suggestedPlan.note && (
                          <div className="rounded-md bg-[#FFF8E1]/60 px-3 py-2.5 text-sm text-main-secondary">
                            <span className="font-medium text-[#8A6D00]">Note: </span>
                            {suggestedPlan.note}
                          </div>
                        )}
                      </div>
                    </section>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                    {/* Left: route & map */}
                    <div className="lg:col-span-5 space-y-5">
                      <DetailSection title="Route" icon={MapPinIcon} action={
                        <span className="inline-flex items-center rounded-md bg-main-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-main-primary">{milesLabel}</span>
                      } bodyClassName="">
                        <ol className="relative p-5 space-y-5">
                          <div className="absolute left-[29px] top-8 bottom-8 border-l-2 border-dashed border-main-base" />
                          <li className="relative flex gap-3.5">
                            <span className="relative z-10 mt-0.5 h-5 w-5 shrink-0 rounded-full bg-emerald-100 ring-4 ring-white flex items-center justify-center">
                              <span className="h-2 w-2 rounded-full bg-emerald-500" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="text-[11px] font-semibold text-emerald-700">Pickup</p>
                              <p className="text-sm font-semibold text-main-dark">
                                <Moment format="ddd, MMM D, YYYY · h:mm A" tz={timeZone || undefined}>{selectedTrip.scheduleTime}</Moment>
                              </p>
                              {suggestedTime && (
                                <p className="mt-1 inline-flex items-center gap-1 rounded-md bg-[#FFF8E1] px-2 py-0.5 text-xs font-semibold text-[#8A6D00] ring-1 ring-[#D0AA00]/40">
                                  <ClockIcon className="h-3.5 w-3.5" />
                                  Sugg. Time:{" "}
                                  <Moment format="ddd, MMM D, YYYY · h:mm A" tz={timeZone || undefined}>{suggestedTime}</Moment>
                                </p>
                              )}
                              <p className="mt-1 text-sm text-main-secondary leading-snug">{selectedTrip.jobOriginAddress || "—"}</p>
                              <AddressAdditionalDetails info={selectedTrip.originAddressAdditional} />
                            </div>
                          </li>
                          <li className="relative flex gap-3.5">
                            <span className="relative z-10 mt-0.5 h-5 w-5 shrink-0 rounded-full bg-rose-100 ring-4 ring-white flex items-center justify-center">
                              <span className="h-2 w-2 rounded-full bg-rose-500" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="text-[11px] font-semibold text-rose-700">Dropoff</p>
                              <p className="text-sm font-semibold text-main-dark">
                                <Moment format="ddd, MMM D, YYYY · h:mm A" tz={timeZone || undefined}>{selectedTrip.appointmentTime}</Moment>
                              </p>
                              <p className="mt-1 text-sm text-main-secondary leading-snug">{selectedTrip.jobDestinationAddress || "—"}</p>
                              <AddressAdditionalDetails info={selectedTrip.destinationAddressAdditional} />
                            </div>
                          </li>
                        </ol>

                        <div className="h-[260px] w-full border-t border-main-base relative bg-main-base-50">
                          <MapView onLoad={handleMapLoad}>
                            {hasRouteCoordinates && (
                              <>
                                <MarkerF
                                  position={{ lat: selectedTrip.jobOriginLatitude, lng: selectedTrip.jobOriginLongitude }}
                                  icon={{
                                    url: pickupMarker,
                                    scaledSize: { width: 32, height: 32 },
                                  }}
                                />
                                <MarkerF
                                  position={{ lat: selectedTrip.jobDestinationLatitude, lng: selectedTrip.jobDestinationLongitude }}
                                  icon={{
                                    url: dropoffMarker,
                                    scaledSize: { width: 32, height: 32 },
                                  }}
                                />
                              </>
                            )}
                          </MapView>
                        </div>
                      </DetailSection>
                    </div>

                    {/* Right: general info, passenger, driver, notes */}
                    <div className="lg:col-span-7 space-y-5">
                      <DetailSection title="General" icon={InformationCircleIcon}>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                          <DetailField label="Trip ID">{selectedTrip.tripId || "—"}</DetailField>
                          <DetailField label="Created At">
                            {selectedTrip.createdAt ? (
                              <Moment format="MM/DD/YYYY · h:mm A" tz={timeZone || undefined}>{selectedTrip.createdAt}</Moment>
                            ) : "—"}
                          </DetailField>
                          <DetailField label="Funding Source">{fundingSourceName || "—"}</DetailField>
                          <DetailField label="Level of Service">{levelOfServiceLabel}</DetailField>
                          <DetailField label="Distance (Est.)">
                            <span className="text-main-primary">{milesLabel}</span>
                          </DetailField>
                          {suggestedTime && (
                            <DetailField label="Suggested Time">
                              <span className="text-[#8A6D00]">
                                <Moment format="MM/DD/YYYY · h:mm A" tz={timeZone || undefined}>{suggestedTime}</Moment>
                              </span>
                            </DetailField>
                          )}
                          {selectedTrip.intakeStatus && (
                            <DetailField label="Intake Status">
                              <span className="capitalize">{formatIntakeStatus(selectedTrip.intakeStatus)}</span>
                            </DetailField>
                          )}
                        </dl>
                      </DetailSection>

                      <DetailSection title="Passenger" icon={UserIcon}>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                          <DetailField label="Full Name">{member?.displayName || tripClientName || "—"}</DetailField>
                          <DetailField label="Client ID (MRN)">{member?.clientId || tripClient?.clientId || "—"}</DetailField>
                          <DetailField label="Phone Number">{member?.contactNumber || tripClient?.contactNumber || "—"}</DetailField>
                          <DetailField label="Cell Phone">{member?.cellPhone || tripClient?.cellPhone || "—"}</DetailField>
                          <DetailField label="Email Address">{member?.email || tripClient?.email || "—"}</DetailField>
                          <DetailField label="Date of Birth">
                            {(member?.dob || tripClient?.dob) ? (
                              <Moment format="MM/DD/YYYY">{member?.dob || tripClient?.dob}</Moment>
                            ) : "—"}
                          </DetailField>
                          <DetailField label="Gender">
                            <span className="capitalize">
                              {member?.gender || tripDetail?.client?.gender || selectedTrip?.priorityClient?.gender || "—"}
                            </span>
                          </DetailField>
                        </dl>
                      </DetailSection>

                      {selectedTrip.driver?.displayName && (
                        <DetailSection title="Driver" icon={TruckIcon}>
                          <div className="flex items-center gap-4">
                            <div className="h-12 w-12 shrink-0 rounded-full bg-main-primary/10 ring-2 ring-white shadow flex items-center justify-center overflow-hidden">
                              {selectedTrip.driver.profileImageURL ? (
                                <img
                                  src={selectedTrip.driver.profileImageURL}
                                  alt={selectedTrip.driver.displayName}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <span className="text-lg font-bold text-main-primary">
                                  {selectedTrip.driver.displayName.charAt(0).toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-main-dark truncate">
                                {selectedTrip.driver.displayName}
                              </p>
                              {selectedTrip.driver.contactNumber && (
                                <a
                                  href={`tel:${selectedTrip.driver.contactNumber}`}
                                  className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-main-primary hover:underline"
                                >
                                  <PhoneIcon className="h-3.5 w-3.5" />
                                  {selectedTrip.driver.contactNumber}
                                </a>
                              )}
                            </div>
                          </div>
                        </DetailSection>
                      )}

                      <DetailSection title="Notes" icon={DocumentTextIcon}>
                        <p className="text-sm text-main-secondary whitespace-pre-wrap leading-relaxed">
                          {selectedTrip.companyNote || selectedTrip.note || (
                            <span className="text-main-light italic">No driver notes added to this trip.</span>
                          )}
                        </p>
                      </DetailSection>
                    </div>
                  </div>
                </DialogBody>
              </div>
            </>
          );
        })()}
      </Dialog>

      {cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4">
          <div className="w-full max-w-xs bg-white/95 backdrop-blur-xl rounded-3xl shadow-[0_20px_60px_rgb(0,0,0,0.2)] overflow-hidden">
            <div className="px-6 pt-7 pb-5 text-center space-y-2">
              <div className="mx-auto h-12 w-12 rounded-2xl bg-red-50 flex items-center justify-center">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">Cancel</h3>
              <p className="text-sm text-gray-500">
                Are you sure you want to cancel this trip? This action cannot be undone.
              </p>
            </div>
            <div className="grid grid-cols-2 divide-x divide-gray-200 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setCancelTarget(null)}
                disabled={cancelling}
                className="py-3.5 text-sm font-medium text-gray-500 hover:bg-gray-50 transition disabled:opacity-50"
              >
                Keep Trip
              </button>
              <button
                type="button"
                onClick={handleCancelTrip}
                disabled={cancelling}
                className="py-3.5 text-sm font-semibold text-red-600 hover:bg-gray-50 transition disabled:opacity-50"
              >
                {cancelling ? "Cancelling..." : "Yes, Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      {willCallTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm bg-white/95 backdrop-blur-xl rounded-3xl shadow-[0_20px_60px_rgb(0,0,0,0.2)] overflow-hidden">
            <div className="flex items-center justify-between px-6 pt-6 pb-2">
              <h3 className="text-base font-semibold text-gray-900">Activate Will Call</h3>
              <button
                type="button"
                onClick={closeWillCallPopup}
                disabled={activatingWillCall}
                className="text-gray-400 hover:text-gray-500 transition outline-none p-1 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 pb-2 pt-2 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                  Pickup Date &amp; Time
                </label>
                <DateTimePicker
                  value={willCallPreScheduleTime}
                  onChange={setWillCallPreScheduleTime}
                  placeholder="Select pickup date & time"
                  disabled={activatingWillCall}
                  minDate={new Date()}
                  className="w-full rounded-2xl bg-gray-100 px-4 py-3.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#293562] focus:bg-white transition disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                  Notes
                </label>
                <textarea
                  rows={3}
                  value={willCallNote}
                  onChange={(event) => setWillCallNote(event.target.value)}
                  disabled={activatingWillCall}
                  className="w-full rounded-2xl bg-gray-100 px-4 py-3.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#293562] focus:bg-white transition disabled:opacity-50 resize-none"
                />
              </div>
            </div>
            <div className="px-6 pb-6 pt-4">
              <button
                type="button"
                onClick={handleActivateWillCall}
                disabled={!willCallPreScheduleTime || activatingWillCall}
                className="w-full rounded-2xl bg-[#293562] py-3.5 text-sm font-semibold text-white shadow-sm hover:brightness-95 transition disabled:opacity-50"
              >
                {activatingWillCall ? "Activating..." : "Activate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4">
          <div className="w-full max-w-xs bg-white/95 backdrop-blur-xl rounded-3xl shadow-[0_20px_60px_rgb(0,0,0,0.2)] overflow-hidden">
            <div className="px-6 pt-7 pb-5 text-center space-y-2">
              <div className="mx-auto h-12 w-12 rounded-2xl bg-emerald-50 flex items-center justify-center">
                <CheckCircleIcon className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">Confirm Trip</h3>
              <p className="text-sm text-gray-500">
                Are you sure you want to confirm this trip?
              </p>
            </div>
            <div className="grid grid-cols-2 divide-x divide-gray-200 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setConfirmTarget(null)}
                disabled={confirming}
                className="py-3.5 text-sm font-medium text-gray-500 hover:bg-gray-50 transition disabled:opacity-50"
              >
                No, Keep
              </button>
              <button
                type="button"
                onClick={handleConfirmIntake}
                disabled={confirming}
                className="py-3.5 text-sm font-semibold text-emerald-600 hover:bg-gray-50 transition disabled:opacity-50"
              >
                {confirming ? "Confirming..." : "Yes, Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {unconfirmTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4">
          <div className="w-full max-w-xs bg-white/95 backdrop-blur-xl rounded-3xl shadow-[0_20px_60px_rgb(0,0,0,0.2)] overflow-hidden">
            <div className="px-6 pt-7 pb-5 text-center space-y-2">
              <div className="mx-auto h-12 w-12 rounded-2xl bg-red-50 flex items-center justify-center">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">Cancel</h3>
              <p className="text-sm text-gray-500">
                Are you sure you want to cancel this trip? This action cannot be undone.
              </p>
            </div>
            <div className="grid grid-cols-2 divide-x divide-gray-200 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setUnconfirmTarget(null)}
                disabled={unconfirming}
                className="py-3.5 text-sm font-medium text-gray-500 hover:bg-gray-50 transition disabled:opacity-50"
              >
                Keep Trip
              </button>
              <button
                type="button"
                onClick={handleUnconfirmIntake}
                disabled={unconfirming}
                className="py-3.5 text-sm font-semibold text-red-600 hover:bg-gray-50 transition disabled:opacity-50"
              >
                {unconfirming ? "Cancelling..." : "Yes, Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>

  );
}
